import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { formatRial } from '@/lib/formatMoney';
import {
  accountingApi,
  type LookupBeneficiary,
  type LookupExpenseType,
  type LookupProject,
} from '@/lib/api/accounting';
import {
  bsiInternetBankApi,
  type BsiIbLastThirtyItem,
  type BsiStatementRegistrationSummary,
} from '@/lib/banks/bsi/api';

type Props = {
  open: boolean;
  accountNumber: string;
  reportSource: 'last_thirty' | 'date_to_date';
  item: BsiIbLastThirtyItem | null;
  existing?: BsiStatementRegistrationSummary | null;
  onClose: () => void;
  onSaved: (registration: BsiStatementRegistrationSummary) => void;
};

const beneficiaryLabel = (type?: string | null) => {
  if (type === 'client') return 'مشتری';
  if (type === 'employee') return 'کارمند';
  if (type === 'company') return 'شرکت';
  if (type === 'other') return 'سایر';
  return 'طرف حساب';
};

const BsiRegisterStatementModal: React.FC<Props> = ({
  open,
  accountNumber,
  reportSource,
  item,
  existing,
  onClose,
  onSaved,
}) => {
  const [projects, setProjects] = React.useState<LookupProject[]>([]);
  const [beneficiaries, setBeneficiaries] = React.useState<LookupBeneficiary[]>([]);
  const [expenseTypes, setExpenseTypes] = React.useState<LookupExpenseType[]>([]);
  const [projectId, setProjectId] = React.useState('');
  const [expenseTypeId, setExpenseTypeId] = React.useState('');
  const [beneficiary, setBeneficiary] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [loadingLookups, setLoadingLookups] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setLoadingLookups(true);
    setError(null);
    accountingApi
      .lookups()
      .then((res) => {
        setProjects(res.projects || []);
        setBeneficiaries(res.beneficiaries || []);
        setExpenseTypes(res.expense_types || []);
      })
      .catch(() => setError('بارگذاری لیست پروژه، طرف‌حساب و نوع هزینه ناموفق بود.'))
      .finally(() => setLoadingLookups(false));
  }, [open]);

  React.useEffect(() => {
    if (!open || !item) return;
    setProjectId(existing?.project_id ? String(existing.project_id) : '');
    setExpenseTypeId(existing?.expense_type_id ? String(existing.expense_type_id) : '');
    if (existing?.counterparty_type === 'other') {
      setBeneficiary('other');
    } else if (existing?.counterparty_type && existing.counterparty_id) {
      setBeneficiary(`${existing.counterparty_type}-${existing.counterparty_id}`);
    } else {
      setBeneficiary('');
    }
    setNotes(existing?.notes || '');
    setError(null);
  }, [open, item, existing]);

  const handleSave = async () => {
    if (!item) return;
    const isDebit = item.debit > 0;
    const isEmployee = beneficiary.startsWith('employee-');
    if (isDebit && isEmployee && !projectId) {
      setError('برای ثبت برداشت بانکی روی حساب کارمند، انتخاب پروژه الزامی است تا در مساعده/علی‌الحساب همان شخص بنشیند.');
      return;
    }
    if (isDebit && isEmployee && !expenseTypeId) {
      setError('نوع هزینه را مشخص کنید تا مثل مساعده/علی‌الحساب در حساب شخص ثبت شود.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await bsiInternetBankApi.storeStatementRegistration({
        account_number: accountNumber,
        report_source: reportSource,
        item,
        project_id: projectId ? Number(projectId) : null,
        expense_type_id: expenseTypeId ? Number(expenseTypeId) : null,
        beneficiary: beneficiary || undefined,
        notes: notes.trim() || undefined,
      });
      onSaved(res.data);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ثبت ردیف ناموفق بود.');
    } finally {
      setSaving(false);
    }
  };

  const directionLabel = item && item.credit > 0 ? 'واریز' : item && item.debit > 0 ? 'برداشت' : 'تراکنش';
  const amount = item ? (item.credit > 0 ? item.credit : item.debit) : 0;
  const beneficiaryType = beneficiary === 'other' ? 'other' : beneficiary.split('-')[0];
  const employeeDebitNeedsProject = Boolean(item && item.debit > 0 && beneficiary.startsWith('employee-'));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="grid w-[min(32rem,calc(100vw-2rem))] min-w-0 max-w-lg grid-cols-1 overflow-hidden">
        <DialogHeader className="min-w-0">
          <DialogTitle>{existing?.registered ? 'ویرایش ثبت گردش' : 'ثبت ردیف گردش بانکی'}</DialogTitle>
        </DialogHeader>

        {item ? (
          <div className="min-w-0 w-full space-y-4">
            <div className="min-w-0 overflow-hidden rounded-md border bg-slate-50 px-3 py-2 text-sm space-y-1">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                <span className="shrink-0">{item.date || '—'} {item.time || ''}</span>
                <span className="shrink-0">سند: {item.doc_no || '—'}</span>
                <span className="shrink-0">{directionLabel}: {amount ? formatRial(amount) : '—'}</span>
              </div>
              <p className="break-all text-sm text-slate-800 line-clamp-2" title={item.description}>
                {item.description || '—'}
              </p>
            </div>

            {loadingLookups ? (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                در حال بارگذاری…
              </div>
            ) : (
              <>
                <div className="min-w-0 space-y-2">
                  <Label>{employeeDebitNeedsProject ? 'پروژه *' : 'پروژه (اختیاری)'}</Label>
                  <SearchableSelect
                    value={projectId}
                    onChange={setProjectId}
                    options={[
                      { value: '', label: 'بدون پروژه' },
                      ...projects.map((p) => ({ value: String(p.id), label: p.label })),
                    ]}
                    placeholder="انتخاب پروژه"
                    popoverClassName="z-[120]"
                  />
                  {employeeDebitNeedsProject ? (
                    <p className="text-xs text-amber-700">
                      با انتخاب کارمند، این برداشت در حساب مساعده/علی‌الحساب همان شخص ثبت می‌شود.
                    </p>
                  ) : null}
                </div>
                <div className="min-w-0 space-y-2">
                  <Label>{employeeDebitNeedsProject ? 'نوع هزینه *' : 'نوع هزینه (اختیاری)'}</Label>
                  <SearchableSelect
                    value={expenseTypeId}
                    onChange={setExpenseTypeId}
                    options={[
                      { value: '', label: 'بدون نوع هزینه' },
                      ...expenseTypes.map((t) => ({ value: String(t.id), label: t.title })),
                    ]}
                    placeholder="انتخاب نوع هزینه"
                    popoverClassName="z-[120]"
                    emptyMessage="نوع هزینه‌ای یافت نشد"
                  />
                </div>
                <div className="min-w-0 space-y-2">
                  <Label>{beneficiaryLabel(beneficiaryType)} (اختیاری)</Label>
                  <SearchableSelect
                    value={beneficiary}
                    onChange={setBeneficiary}
                    options={[
                      { value: '', label: 'انتخاب نشده' },
                      { value: 'other', label: 'سایر' },
                      ...beneficiaries.map((b) => ({
                        value: b.id,
                        label: `${b.name} (${b.type === 'client' ? 'مشتری' : b.type === 'employee' ? 'کارمند' : 'شرکت'})`,
                      })),
                    ]}
                    placeholder="مشتری / کارمند / شرکت / سایر"
                    popoverClassName="z-[120]"
                  />
                </div>
                <div className="min-w-0 space-y-2">
                  <Label>یادداشت</Label>
                  <Textarea
                    className="max-w-full resize-none"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="توضیح تکمیلی…"
                    rows={3}
                  />
                </div>
              </>
            )}

            {error ? <p className="text-sm text-rose-600">{error}</p> : null}
          </div>
        ) : null}

        <DialogFooter className="min-w-0 gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            انصراف
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving || !item || loadingLookups}>
            {saving ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
            {existing?.registered ? 'ذخیره تغییرات' : 'ثبت'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BsiRegisterStatementModal;
